export const getDenmarkTime = (): Date => {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }));
};

export const getCutoffConfig = () => ({
    hour: parseInt(process.env.NEXT_PUBLIC_CUTOFF_HOUR || '10', 10),
    minute: parseInt(process.env.NEXT_PUBLIC_CUTOFF_MINUTE || '30', 10),
});

export const getCutoffTime = (cutoffHour?: number, cutoffMinute?: number): Date => {
    const cfg = getCutoffConfig();
    const hour = cutoffHour ?? cfg.hour;
    const minute = cutoffMinute ?? cfg.minute;
    const denmarkTime = getDenmarkTime();
    const cutoff = new Date(denmarkTime);
    cutoff.setHours(hour, minute, 0, 0);
    return cutoff;
};

export const isWithinCutoff = (cutoff: Date): boolean => {
    const now = getDenmarkTime();
    return now < cutoff;
};

export const getCurrentCutoffState = () => {
    const denmarkNow = getDenmarkTime();
    const cutoff = getCutoffTime();
    const isWithinCutoff = denmarkNow < cutoff;

    return {
        denmarkNow,
        cutoff,
        isWithinCutoff,
    };
};

// Convenience function for just the boolean check
export const isCurrentlyWithinCutoff = (): boolean => {
    const { isWithinCutoff } = getCurrentCutoffState();
    return isWithinCutoff;
};

export const formatTimeDa = (date: Date): string => {
    return date.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

export type CutoffComputed = {
    cutoff: Date;
    now: Date;
    isWithinCutoff: boolean;
    cutoffText: string;
    timeRemainingText: string | null;
};

export type DeliveryState =
    | 'today'
    | 'tomorrow'
    | 'sunday'
    | 'christmasEve'
    | 'christmasEveTomorrow';

export type DeliveryStateComputed = {
    deliveryState: DeliveryState;
    cutoff: Date;
    now: Date;
    cutoffText: string;
    timeRemainingText: string | null;
    isWithinCutoff: boolean;
};

export const computeCutoffState = (cutoffHour?: number, cutoffMinute?: number): CutoffComputed => {
    const now = getDenmarkTime();
    const cutoff = getCutoffTime(cutoffHour, cutoffMinute);
    const within = now < cutoff;
    const cutoffText = formatTimeDa(cutoff);

    let timeRemainingText: string | null = null;
    if (within) {
        const diff = cutoff.getTime() - now.getTime();
        if (diff > 0) {
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            if (hours > 0) timeRemainingText = `${hours}t ${minutes}m`;
            else if (minutes > 0) timeRemainingText = `${minutes}m`;
            else timeRemainingText = `under 1m`;
        }
    }

    return { cutoff, now, isWithinCutoff: within, cutoffText, timeRemainingText };
};

// Helper function to check if it's December 22nd or 23rd
const isChristmasEvePeriod = (date: Date): boolean => {
    const month = date.getMonth(); // 0-11, December is 11
    const day = date.getDate();
    return month === 11 && (day === 22 || day === 23);
};

// Helper function to check if it's December 23rd (after cutoff), 24th, 25th, or 26th
const isPostChristmasEvePeriod = (date: Date): boolean => {
    const month = date.getMonth(); // 0-11, December is 11
    const day = date.getDate();
    return month === 11 && (day === 24 || day === 25 || day === 26);
};

// Helper function to check if it's December 31st (after cutoff) or January 1st
const isNewYearPeriod = (date: Date, isWithinCutoff: boolean): boolean => {
    const month = date.getMonth(); // 0-11, December is 11
    const day = date.getDate();
    const isNewYearsEveAfterCutoff = month === 11 && day === 31 && !isWithinCutoff;
    const isNewYearsDay = month === 0 && day === 1;
    return isNewYearsEveAfterCutoff || isNewYearsDay;
};

// Helper function to calculate time remaining until next day's cutoff
const getTimeUntilNextDayCutoff = (
    now: Date,
    cutoffHour: number,
    cutoffMinute: number,
): string | null => {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(cutoffHour, cutoffMinute, 0, 0);

    const diff = tomorrow.getTime() - now.getTime();
    if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) return `${hours}t ${minutes}m`;
        else if (minutes > 0) return `${minutes}m`;
        else return `under 1m`;
    }
    return null;
};

export const computeDeliveryState = (
    cutoffHour?: number,
    cutoffMinute?: number,
): DeliveryStateComputed => {
    const now = getDenmarkTime();
    const cutoff = getCutoffTime(cutoffHour, cutoffMinute);
    const cutoffText = formatTimeDa(cutoff);

    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const isWithinCutoff = now < cutoff;
    const day = now.getDate();

    let deliveryState: DeliveryState = 'today'; // Default fallback
    let timeRemainingText: string | null = null;

    // Special Christmas Eve logic for December 22nd and 23rd
    if (isChristmasEvePeriod(now)) {
        if (day === 22) {
            // December 22nd
            if (isWithinCutoff) {
                deliveryState = 'christmasEve';
                // Countdown until cutoff time
                const diff = cutoff.getTime() - now.getTime();
                if (diff > 0) {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    if (hours > 0) timeRemainingText = `${hours}t ${minutes}m`;
                    else if (minutes > 0) timeRemainingText = `${minutes}m`;
                    else timeRemainingText = `under 1m`;
                }
            } else {
                deliveryState = 'christmasEveTomorrow';
                // Countdown until December 23rd cutoff
                const cfg = getCutoffConfig();
                timeRemainingText = getTimeUntilNextDayCutoff(now, cfg.hour, cfg.minute);
            }
        } else if (day === 23) {
            // December 23rd
            if (isWithinCutoff) {
                deliveryState = 'christmasEveTomorrow';
                // Countdown until cutoff time
                const diff = cutoff.getTime() - now.getTime();
                if (diff > 0) {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    if (hours > 0) timeRemainingText = `${hours}t ${minutes}m`;
                    else if (minutes > 0) timeRemainingText = `${minutes}m`;
                    else timeRemainingText = `under 1m`;
                }
            } else {
                // December 23rd after cutoff = Sunday delivery
                deliveryState = 'sunday';
            }
        }
    } else if (isPostChristmasEvePeriod(now)) {
        // December 24th, 25th, or 26th = Sunday delivery
        deliveryState = 'sunday';
    } else if (isNewYearPeriod(now, isWithinCutoff)) {
        // December 31st after cutoff or January 1st = Sunday delivery
        deliveryState = 'sunday';
    } else {
        // Regular delivery logic for non-Christmas Eve period
        if (dayOfWeek === 5) {
            // Friday
            if (isWithinCutoff) {
                deliveryState = 'today';
            } else {
                // Friday after cutoff = Sunday delivery
                deliveryState = 'sunday';
            }
        } else if (dayOfWeek === 6) {
            // Saturday
            // Saturday = Sunday delivery
            deliveryState = 'sunday';
        } else if (dayOfWeek === 0) {
            // Sunday
            if (isWithinCutoff) {
                // Sunday before cutoff = Sunday delivery
                deliveryState = 'sunday';
            } else {
                // Sunday after cutoff = tomorrow (Monday)
                deliveryState = 'tomorrow';
            }
        } else {
            // Monday, Tuesday, Wednesday, Thursday
            if (isWithinCutoff) {
                deliveryState = 'today';
            } else {
                deliveryState = 'tomorrow';
            }
        }

        // Calculate time remaining only for 'today' delivery
        if (deliveryState === 'today' && isWithinCutoff) {
            const diff = cutoff.getTime() - now.getTime();
            if (diff > 0) {
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                if (hours > 0) timeRemainingText = `${hours}t ${minutes}m`;
                else if (minutes > 0) timeRemainingText = `${minutes}m`;
                else timeRemainingText = `under 1m`;
            }
        }
    }

    return {
        deliveryState,
        cutoff,
        now,
        cutoffText,
        timeRemainingText,
        isWithinCutoff,
    };
};
